import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import PagePreview from "../components/PagePreview/PagePreview";
import Spinner from "../components/Spinner/Spinner";
// ProgressBar handled by OnboardingLayout
import styles from "../styles/businessDesign.module.css";

const SOCIALS = [
  { key: "tiktok", label: "TikTok", placeholder: "@yourhandle" },
  { key: "instagram", label: "Instagram", placeholder: "@yourhandle" },
  { key: "facebook", label: "Facebook", placeholder: "שם עמוד" },
  { key: "whatsapp", label: "WhatsApp", placeholder: "05XXXXXXXX" },
  { key: "phone", label: "טלפון", placeholder: "05XXXXXXXX" },
];

const BIZ_COLORS = [
  "#007BFF", "#E273AA", "#111827", "#28A745", "#C82333",
  "#FD7E14", "#D00254", "#9932CC", "#884A39",
];


// The server returns uploaded images as relative paths ("gallery_image/x.jpg")
const imgUrl = (p) => (!p ? null : /^(https?:|blob:|data:)/.test(p) ? p : `${BASE_URL}/uploads/${String(p).replace(/^\//, "")}`);

// An image that reports a failed load even when it failed before React's
// onError was attached (a cached 404 does that) — so the "+" placeholder shows.
const SafeImg = ({ src, className, onFail }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el && el.complete && el.naturalWidth === 0) onFail();
  }, [src]);
  return <img ref={ref} src={src} alt="" className={className} onError={onFail} />;
};

const BusinessDesign = ({ onNext, onBack }) => {
  const coverRef = useRef(null);
  const logoRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [coverPreview, setCoverPreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [bio, setBio] = useState("");
  const [bizName, setBizName] = useState("");
  const [galleryPaths, setGalleryPaths] = useState([]);
  // Editor opened by tapping a part of the live page preview: name | description | contact
  const [editField, setEditField] = useState(null);
  const [draft, setDraft] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const galleryRef = useRef(null);
  const [businessHighlights, setBusinessHighlights] = useState([]);
  const [bizColor, setBizColor] = useState("#0f172a");
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [socialLinks, setSocialLinks] = useState({
    tiktok: "",
    instagram: "",
    facebook: "",
    whatsapp: "",
    phone: "",
  });
  const [activeSocial, setActiveSocial] = useState(null);
  // Bumped after a new cover/logo is uploaded so the live preview reloads
  const [previewKey, setPreviewKey] = useState(0);

  const businessID = localStorage.getItem("business_id");
  const authToken = localStorage.getItem("auth_token");
  const businessName = localStorage.getItem("ownerName") || "";

  // Load existing business data (again after an Instagram import)
  const loadProfile = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/business/profile_data`, {
          headers: { Authorization: authToken },
        });
        const d = res.data?.data || res.data;
        if (d) {
          setBizName(d.business_name || "");
          setGalleryPaths(Array.isArray(d.multiImage) ? d.multiImage.filter(Boolean) : []);
          setBio(d.description || "");
          if (Array.isArray(d.businessHighlights) && d.businessHighlights.length > 0) {
            setBusinessHighlights(d.businessHighlights);
          }
          setCoverPreview(imgUrl(d.business_image));
          setLogoPreview(imgUrl(d.profile));
          setBizColor(d.color || "#0f172a");
          setSocialLinks({
            tiktok: d.tiktok || "",
            instagram: d.instagramId || "",
            facebook: d.facebook || "",
            whatsapp: d.whatsApp_phone || d.business_phone || "",
            phone: d.business_phone || "",
          });
        }
      } catch (e) {
        console.log("Load profile error:", e);
      }
  };

  useEffect(() => {
    if (!authToken) {
      if (onBack) onBack();
      return;
    }
    loadProfile();
  }, []);

  // "ייבוא מאינסטגרם": the server reads the public profile and fills what's empty
  const [igUrl, setIgUrl] = useState("");
  const [igState, setIgState] = useState({ busy: false, msg: "", ok: false });
  const importInstagram = async () => {
    if (!igUrl.trim() || igState.busy) return;
    setIgState({ busy: true, msg: "", ok: false });
    try {
      const res = await axios.post(
        `${BASE_URL}/onboarding/instagram-import`,
        { url: igUrl.trim() },
        { headers: { Authorization: authToken } }
      );
      const i = res.data?.imported || {};
      const parts = [
        i.logo && "תמונת פרופיל",
        i.cover && "תמונת קאבר",
        i.gallery && `${i.gallery} תמונות לגלריה`,
        i.description && "תיאור",
        i.phone && "טלפון",
      ].filter(Boolean);
      await loadProfile();
      setPreviewKey((k) => k + 1);
      setIgState({
        busy: false,
        ok: true,
        msg: parts.length ? `ייבאנו: ${parts.join(", ")} ✓` : "הפרופיל חובר, לא היה מה להוסיף",
      });
    } catch (e) {
      setIgState({ busy: false, ok: false, msg: e?.response?.data?.message || "לא הצלחנו לייבא, נסו שוב" });
    }
  };

  const generateAiDescription = async () => {
    if (!aiInput.trim()) return;
    setAiLoading(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/ai/generate-description`,
        { input: aiInput.trim(), businessName: businessName || "" },
        { headers: { Authorization: authToken } }
      );
      if (res.data?.text) {
        setBio(res.data.text);
        const highlights = Array.isArray(res.data.highlights) ? res.data.highlights.slice(0, 4) : null;
        if (highlights && highlights.length > 0) setBusinessHighlights(highlights);
        // Saved right away so the live preview shows it
        saveFields({ description: res.data.text, ...(highlights && highlights.length ? { businessHighlights: highlights } : {}) });
        setShowAiModal(false);
        setAiInput("");
      }
    } catch (e) {
      console.log("AI error:", e);
    }
    setAiLoading(false);
  };

  // Photos are saved as soon as they're picked so the live preview shows them
  const uploadNow = async (field, file) => {
    try {
      const formData = new FormData();
      formData.append("id", businessID);
      formData.append(field === "logo" ? "profile" : field, file);
      await axios.put(`${BASE_URL}/business/update_business_cover_profile`, formData, {
        headers: { Authorization: authToken, "Content-Type": "multipart/form-data" },
      });
      if (field === "cover") setCoverFile(null);
      else setLogoFile(null);
      setPreviewKey((k) => k + 1);
    } catch (err) {
      // Rejected (e.g. an unsupported format): say so and show what's really saved
      if (field === "cover") setCoverFile(null);
      else setLogoFile(null);
      window.alert(err?.response?.data?.message || "התמונה לא נשמרה, נסו תמונה אחרת");
      loadProfile();
    }
  };

  const handleCover = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
    uploadNow("cover", file);
  };

  const handleLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    uploadNow("logo", file);
  };

  // Saves a few profile fields now and reloads the live preview
  const saveFields = async (fields) => {
    try {
      await axios.put(
        `${BASE_URL}/business/update_profile_info`,
        { id: businessID, ...fields },
        { headers: { Authorization: authToken } }
      );
      await loadProfile();
      setPreviewKey((k) => k + 1);
      return true;
    } catch (e) {
      console.log("Profile save error:", e);
      return false;
    }
  };

  // Tapping a part of the live preview: photos open the file picker, the
  // rest open a small editor with the current value
  const openEditor = (field) => {
    if (field === "cover") return coverRef.current?.click();
    if (field === "logo") return logoRef.current?.click();
    if (field === "gallery") return galleryRef.current?.click();
    if (field === "name") setDraft({ business_name: bizName });
    else if (field === "description") setDraft({ description: bio });
    else if (field === "contact") setDraft({ ...socialLinks });
    else return;
    setEditField(field);
  };

  const saveEditor = async () => {
    setEditSaving(true);
    let fields = draft;
    if (editField === "contact") {
      setSocialLinks(draft);
      fields = {
        business_phone: draft.phone,
        whatsApp_phone: draft.whatsapp,
        instagramId: draft.instagram,
        facebook: draft.facebook,
        tiktok: draft.tiktok,
      };
    }
    const ok = await saveFields(fields);
    setEditSaving(false);
    if (ok) setEditField(null);
  };

  const addGallery = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, Math.max(0, 8 - galleryPaths.length));
    e.target.value = "";
    if (!files.length) return;
    try {
      const formData = new FormData();
      formData.append("id", businessID);
      formData.append("existing_images", JSON.stringify(galleryPaths));
      files.forEach((f) => formData.append("multipleImage", f));
      await axios.put(`${BASE_URL}/business/update_business_cover_profile`, formData, {
        headers: { Authorization: authToken, "Content-Type": "multipart/form-data" },
      });
      await loadProfile();
      setPreviewKey((k) => k + 1);
    } catch (err) {
      window.alert(err?.response?.data?.message || "התמונות לא נשמרו, נסו תמונות אחרות");
    }
  };

  // A design/color applied inside the live preview — keep this step in sync so
  // "סיום" doesn't save the old color over it.
  const onPreviewApplied = ({ color }) => {
    if (!color) return;
    setBizColor(BIZ_COLORS.find((c) => c.toLowerCase() === String(color).toLowerCase()) || color);
  };

  const toggleSocial = (key) => {
    setActiveSocial((prev) => (prev === key ? null : key));
  };

  const updateSocial = (key, value) => {
    setSocialLinks((prev) => ({ ...prev, [key]: value }));
  };

  const handleFinish = async () => {
    setSaving(true);

    // Upload images if selected
    if (coverFile || logoFile) {
      try {
        const formData = new FormData();
        formData.append("id", businessID);
        if (coverFile) formData.append("cover", coverFile);
        if (logoFile) formData.append("profile", logoFile);

        await axios.put(
          `${BASE_URL}/business/update_business_cover_profile`,
          formData,
          {
            headers: {
              Authorization: authToken,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } catch (e) {
        console.log("Image upload error:", e);
      }
    }

    // Save text fields
    try {
      await axios.put(
        `${BASE_URL}/business/update_profile_info`,
        {
          id: businessID,
          description: bio,
          businessHighlights: businessHighlights.filter(h => h.title || h.subtitle),
          color: bizColor,
          isV2: true,
          instagramId: socialLinks.instagram,
          whatsApp_phone: socialLinks.whatsapp,
          facebook: socialLinks.facebook,
          tiktok: socialLinks.tiktok,
          business_phone: socialLinks.phone,
        },
        { headers: { Authorization: authToken } }
      );
    } catch (e) {
      console.log("Profile save error:", e);
    }

    setSaving(false);
    onNext();
  };

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <h2 className={styles.title}>הטאצ׳ הסופי - עיצוב עמוד העסק החדש שלכם</h2>
        <p className={styles.subtitle}>
          כך הלקוחות שלכם יראו אתכם - ניתן לעדכן בכל עת
        </p>

        {/* Instagram import (server reads through Apify) */}
        {(
        <div className={styles.igBox}>
          <div className={styles.igTitle}>יש לכם אינסטגרם? נבנה את העמוד מהתמונות שלכם</div>
          <div className={styles.igRow}>
            <input
              className={styles.igInput}
              dir="ltr"
              inputMode="url"
              placeholder="instagram.com/your.business"
              value={igUrl}
              onChange={(e) => setIgUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && importInstagram()}
            />
            <button type="button" className={styles.igBtn} onClick={importInstagram} disabled={igState.busy || !igUrl.trim()}>
              {igState.busy ? "מייבאים..." : "ייבוא"}
            </button>
          </div>
          {igState.busy && <div className={styles.igHint}>אוספים את התמונות מהפרופיל, זה לוקח כחצי דקה</div>}
          {igState.msg && <div className={igState.ok ? styles.igOk : styles.igErr}>{igState.msg}</div>}
          <div className={styles.igHint}>תמונת פרופיל, תמונות לגלריה ותיאור מהפרופיל הציבורי. אפשר לשנות הכל אחר כך</div>
        </div>
        )}

        {/* Preview Card */}
        <div className={styles.card}>
          {/* Cover */}
          <div
            className={styles.cover}
            onClick={() => coverRef.current?.click()}
          >
            {coverPreview ? (
              <>
                <SafeImg src={coverPreview} className={styles.coverImg} onFail={() => setCoverPreview(null)} />
                <span className={styles.replaceChip}>החלפת תמונה</span>
              </>
            ) : (
              <div className={styles.coverPlaceholder}>
                <span className={styles.plusIcon}>+</span>
                <span>תמונת קאבר</span>
              </div>
            )}
            <input
              ref={coverRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleCover}
            />
          </div>

          {/* Logo - centered */}
          <div className={styles.logoCenterWrap}>
            <div
              className={styles.logoWrap}
              onClick={() => logoRef.current?.click()}
            >
              {logoPreview ? (
                <SafeImg src={logoPreview} className={styles.logoImg} onFail={() => setLogoPreview(null)} />
              ) : (
                <div className={styles.logoPlaceholder}>
                  <span className={styles.plusIcon}>+</span>
                  <span>לוגו</span>
              </div>
            )}
            <input
              ref={logoRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleLogo}
            />
            </div>
          </div>

          {/* Business name */}
          <div className={styles.nameSection}>
            <h3 className={styles.bizName}>{businessName}</h3>
          </div>

          {/* Social Icons */}
          <div className={styles.socialRow}>
            {SOCIALS.map((s) => {
              const hasFill = !!socialLinks[s.key];
              const isActive = activeSocial === s.key;
              return (
                <div
                  key={s.key}
                  className={`${styles.socialIcon} ${
                    hasFill ? styles.socialFilled : ""
                  } ${isActive ? styles.socialActive : ""}`}
                  onClick={() => toggleSocial(s.key)}
                >
                  {!hasFill && <span className={styles.socialPlus}>+</span>}
                  <span className={styles.socialEmoji}>
                    {s.key === "tiktok" && (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9a6.33 6.33 0 00-.79-.05A6.34 6.34 0 003.15 15.3a6.34 6.34 0 0010.86 4.43v-7.15a8.16 8.16 0 005.58 2.17V11.3a4.85 4.85 0 01-1.58-.27V8.16A8.16 8.16 0 0019.59 6.69z"/></svg>
                    )}
                    {s.key === "instagram" && (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                    )}
                    {s.key === "facebook" && (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    )}
                    {s.key === "whatsapp" && (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    )}
                    {s.key === "phone" && (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Active social input */}
          {activeSocial && (
            <div className={styles.socialInput}>
              <label className={styles.socialLabel}>
                {SOCIALS.find((s) => s.key === activeSocial)?.label}
              </label>
              <input
                type="text"
                className={styles.socialField}
                placeholder={
                  SOCIALS.find((s) => s.key === activeSocial)?.placeholder
                }
                value={socialLinks[activeSocial]}
                onChange={(e) => updateSocial(activeSocial, e.target.value)}
                autoFocus
              />
            </div>
          )}

          {/* Bio */}
          <div className={styles.bioHeader}>
            <button
              className={styles.aiBtn}
              onClick={() => setShowAiModal(true)}
              type="button"
            >
              ✨ צריכים עזרה לתאר את העסק?
            </button>
          </div>
          <textarea
            className={styles.bio}
            placeholder="ספרו ללקוחות על העסק שלכם בכמה מילים..."
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={3}
          />

          {/* Business Highlights */}
          <div style={{ marginTop: 16, padding: "0 20px 16px", direction: "rtl" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#1c1c1e" }}>תכונות העסק</span>
              <span style={{ background: "#dbeafe", color: "#2563eb", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10 }}>מומלץ</span>
            </div>
            <p style={{ fontSize: 12, color: "#6b7280", textAlign: "right", margin: "0 0 10px" }}>
              הוסיפו עד 4 נקודות חוזק שמייחדות את העסק שלכם
            </p>
            {businessHighlights.map((hl, idx) => (
              <div key={idx} style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "center" }}>
                <input style={{ width: 38, minWidth: 38, height: 36, border: "1.5px solid #e5e7eb", borderRadius: 10, textAlign: "center", fontSize: 16 }} value={hl.icon} onChange={e => setBusinessHighlights(prev => { const n = [...prev]; n[idx] = {...n[idx], icon: e.target.value}; return n; })} placeholder="🏆" maxLength={2} />
                <input style={{ flex: 1, minWidth: 0, height: 36, border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "0 10px", textAlign: "right", fontSize: 13 }} value={hl.title} onChange={e => setBusinessHighlights(prev => { const n = [...prev]; n[idx] = {...n[idx], title: e.target.value}; return n; })} placeholder="כותרת (עד 3 מילים)" maxLength={20} />
                <input style={{ flex: 1, minWidth: 0, height: 36, border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "0 10px", textAlign: "right", fontSize: 13 }} value={hl.subtitle} onChange={e => setBusinessHighlights(prev => { const n = [...prev]; n[idx] = {...n[idx], subtitle: e.target.value}; return n; })} placeholder="תיאור קצר (עד 4 מילים)" maxLength={30} />
                <button type="button" onClick={() => setBusinessHighlights(prev => prev.filter((_, i) => i !== idx))} style={{ background: "none", border: "none", color: "#ef4444", fontSize: 14, cursor: "pointer", padding: 0, flexShrink: 0 }}>✕</button>
              </div>
            ))}
            {businessHighlights.length < 4 && (
              <button type="button" onClick={() => setBusinessHighlights(prev => [...prev, { icon: "", title: "", subtitle: "" }])} style={{ display: "block", margin: "0 auto", padding: "8px 16px", border: "1.5px dashed #e5e7eb", borderRadius: 10, background: "none", color: "#6b7280", fontSize: 13, cursor: "pointer" }}>
                + הוסף תכונה
              </button>
            )}
          </div>
        </div>

        {/* Color picker */}
        <h4 className={styles.colorLabel}>בחירת צבע לעסק</h4>
        <div className={styles.colorRow}>
          {BIZ_COLORS.map((c) => (
            <div
              key={c}
              className={`${styles.colorCircle} ${
                bizColor === c ? styles.colorCircleActive : ""
              }`}
              style={{ backgroundColor: c }}
              onClick={() => setBizColor(c)}
            >
              {bizColor === c && <span className={styles.colorCheck}>✓</span>}
            </div>
          ))}
        </div>

        {/* Live page: tap any part to edit it, switch designs and colors, apply */}
        {<PagePreview
          src="onboarding"
          color={bizColor}
          onApplied={onPreviewApplied}
          reloadKey={previewKey}
          onEdit={openEditor}
          title="ככה הלקוחות שלכם יראו את העמוד"
          subtitle="לחצו על כל חלק בעמוד כדי לערוך אותו. למעלה אפשר לעבור בין העיצובים והצבעים"
        />}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerInner}>
          <button
            className={styles.finishBtn}
            onClick={handleFinish}
            disabled={saving}
          >
            {saving ? <Spinner /> : "סיום!"}
          </button>
        </div>
      </div>
      {/* AI Modal */}
      <input ref={galleryRef} type="file" accept="image/*" multiple hidden onChange={addGallery} />

      {/* Editor for the part of the preview that was tapped */}
      {editField && (
        <div className={styles.aiBackdrop} onClick={() => !editSaving && setEditField(null)}>
          <div className={styles.aiSheet} onClick={(e) => e.stopPropagation()} dir="rtl">
            <div className={styles.aiSheetHeader}>
              <span className={styles.aiSheetTitle}>
                {editField === "name" ? "שם העסק" : editField === "description" ? "תיאור העסק" : "טלפון ורשתות"}
              </span>
              <button className={styles.aiSheetClose} onClick={() => setEditField(null)}>✕</button>
            </div>
            {editField === "name" && (
              <input
                className={styles.editInput}
                value={draft.business_name || ""}
                onChange={(e) => setDraft({ business_name: e.target.value })}
                placeholder="שם העסק"
                autoFocus
              />
            )}
            {editField === "description" && (
              <>
                <textarea
                  className={styles.editTextarea}
                  value={draft.description || ""}
                  onChange={(e) => setDraft({ description: e.target.value })}
                  placeholder="כמה מילים על העסק"
                  rows={6}
                  autoFocus
                />
                <button
                  type="button"
                  className={styles.editAiLink}
                  onClick={() => {
                    setEditField(null);
                    setShowAiModal(true);
                  }}
                >
                  ✨ לכתוב לי עם AI
                </button>
              </>
            )}
            {editField === "contact" &&
              SOCIALS.map((s) => (
                <label key={s.key} className={styles.editRow}>
                  <span>{s.label}</span>
                  <input
                    className={styles.editInput}
                    dir="ltr"
                    value={draft[s.key] || ""}
                    placeholder={s.placeholder}
                    onChange={(e) => setDraft((d) => ({ ...d, [s.key]: e.target.value }))}
                  />
                </label>
              ))}
            <button type="button" className={styles.editSave} onClick={saveEditor} disabled={editSaving}>
              {editSaving ? "שומרים..." : "שמירה"}
            </button>
          </div>
        </div>
      )}

      {showAiModal && (
        <div className={styles.aiBackdrop} onClick={() => setShowAiModal(false)}>
          <div className={styles.aiSheet} onClick={(e) => e.stopPropagation()}>
            <div className={styles.aiSheetHeader}>
              <span className={styles.aiSheetTitle}>תיאור עסק עם AI</span>
              <button className={styles.aiSheetClose} onClick={() => setShowAiModal(false)}>✕</button>
            </div>
            <p className={styles.aiSheetSub}>כתבו כמה מילים על העסק שלכם ואנחנו נשפר את זה</p>
            <textarea
              className={styles.aiSheetInput}
              placeholder="למשל: מספרה בתל אביב, מתמחים בצבע ותספורות נשים..."
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              rows={3}
              autoFocus
            />
            <button
              className={styles.aiSheetBtn}
              onClick={generateAiDescription}
              disabled={!aiInput.trim() || aiLoading}
            >
              {aiLoading ? "מייצר..." : "יצירת תיאור"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDesign;
